import {
  Modal, Form, Input, Select, Row, Col, InputNumber, Checkbox, Spin,
  AutoComplete, message, Tabs, Button, Alert, Space, Card, Typography, Collapse, Tooltip, theme,
} from 'antd'
import VoiceTextarea from './VoiceTextarea'
import { FileTextOutlined, CameraOutlined, FolderOpenOutlined, PlusOutlined, DeleteOutlined, ApiOutlined, FilePdfOutlined } from '@ant-design/icons'
import PannelloIntegrazioneEsterna from '@/components/PannelloIntegrazioneEsterna'
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateVehicle, useUpdateVehicle } from '@/hooks/useVehicles'
import { useMarche, useModelli, useCarburanti } from '@/hooks/useAuto'
import { useLibrettoOcr } from '@/hooks/useLibrettoOcr'
import type { Vehicle } from '@/types'
import { useState, useEffect, useRef } from 'react'

interface VehicleFormModalProps {
  open: boolean
  onSuccess: (vehicle: Vehicle) => void
  onCancel: () => void
  editingVehicle?: Vehicle | null
}

const VehicleFormModal = ({ open, onSuccess, onCancel, editingVehicle }: VehicleFormModalProps) => {
  const { token } = theme.useToken()
  const [form] = Form.useForm()
  const [selectedMarca, setSelectedMarca] = useState<string>('')
  const [activeTab, setActiveTab] = useState('info')

  // Libretto OCR state
  const [librettoModalOpen, setLibrettoModalOpen] = useState(false)
  const [librettoFile, setLibrettoFile] = useState<File | null>(null)
  const [librettoPreview, setLibrettoPreview] = useState<string | null>(null)
  const [ocrAlertVisible, setOcrAlertVisible] = useState(false)
  const [ocrParziale, setOcrParziale] = useState(false)
  const previewUrlRef = useRef<string | null>(null)
  const [pneumaticiPanelOpen, setPneumaticiPanelOpen] = useState<string[]>([])

  // Camera state for libretto
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cameraFacing, setCameraFacing] = useState<'user' | 'environment'>('environment')
  const [capturedFrame, setCapturedFrame] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const startCamera = async (facing: 'user' | 'environment' = cameraFacing) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) videoRef.current.srcObject = stream
    } catch {
      message.error('Impossibile accedere alla fotocamera')
      setCameraOpen(false)
    }
  }

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    setCapturedFrame(null)
  }

  const snapPhoto = () => {
    if (!videoRef.current || !canvasRef.current) return
    const ctx = canvasRef.current.getContext('2d')
    if (!ctx) return
    canvasRef.current.width = videoRef.current.videoWidth
    canvasRef.current.height = videoRef.current.videoHeight
    ctx.drawImage(videoRef.current, 0, 0)
    setCapturedFrame(canvasRef.current.toDataURL('image/jpeg', 0.9))
  }

  const confirmCameraPhoto = async () => {
    if (!capturedFrame) return
    const blob = await fetch(capturedFrame).then(r => r.blob())
    const file = new File([blob], 'libretto_foto.jpg', { type: 'image/jpeg' })
    stopCamera()
    setCameraOpen(false)
    handleFileSelect(file)
  }

  const switchCamera = () => {
    const next = cameraFacing === 'environment' ? 'user' : 'environment'
    setCameraFacing(next)
    stopCamera()
    setTimeout(() => startCamera(next), 300)
  }

  const queryClient = useQueryClient()
  const createMutation = useCreateVehicle()
  const updateMutation = useUpdateVehicle()
  const { data: marcheData, isLoading: loadingMarche } = useMarche()
  const { data: modelliData, isLoading: loadingModelli } = useModelli(selectedMarca)
  const { data: carburanti } = useCarburanti()
  const { analizza, isLoading: isOcrLoading } = useLibrettoOcr()
  const { settings: integrationSettings } = useIntegrationSettings()

  useEffect(() => {
    if (open && editingVehicle) {
      form.setFieldsValue({
        targa: editingVehicle.targa,
        telaio: editingVehicle.telaio,
        marca: editingVehicle.marca,
        modello: editingVehicle.modello,
        anno: editingVehicle.anno,
        colore: editingVehicle.colore,
        carburante: editingVehicle.carburante,
        km_attuali: editingVehicle.km_attuali,
        courtesy_car: editingVehicle.courtesy_car || false,
        note: editingVehicle.note,
        cilindrata: editingVehicle.cilindrata,
        codice_motore: (editingVehicle as any).codice_motore,
        kw: editingVehicle.kw,
        cv: editingVehicle.cv,
        porte: editingVehicle.porte,
        prima_immatricolazione: editingVehicle.prima_immatricolazione,
        classe_emissioni: editingVehicle.classe_emissioni,
        emissioni_co2: editingVehicle.emissioni_co2,
        posti: editingVehicle.posti,
        carrozzeria: editingVehicle.carrozzeria,
        massa_max_kg: editingVehicle.massa_max_kg,
        data_immatricolazione_attuale: editingVehicle.data_immatricolazione_attuale,
        categoria_veicolo: editingVehicle.categoria_veicolo,
        intestatario_cognome: editingVehicle.intestatario_cognome,
        intestatario_nome: editingVehicle.intestatario_nome,
        intestatario_indirizzo: editingVehicle.intestatario_indirizzo,
        intestatario_compr_cognome: editingVehicle.intestatario_compr_cognome,
        intestatario_compr_nome: editingVehicle.intestatario_compr_nome,
        intestatario_compr_indirizzo: editingVehicle.intestatario_compr_indirizzo,
        proprietario_cognome: editingVehicle.proprietario_cognome,
        proprietario_nome: editingVehicle.proprietario_nome,
        proprietario_indirizzo: editingVehicle.proprietario_indirizzo,
        proprietario_compr_cognome: editingVehicle.proprietario_compr_cognome,
        proprietario_compr_nome: editingVehicle.proprietario_compr_nome,
        proprietario_compr_indirizzo: editingVehicle.proprietario_compr_indirizzo,
        utilizzatore_cognome: editingVehicle.utilizzatore_cognome,
        utilizzatore_nome: editingVehicle.utilizzatore_nome,
        utilizzatore_indirizzo: editingVehicle.utilizzatore_indirizzo,
        utilizzatore_compr_cognome: editingVehicle.utilizzatore_compr_cognome,
        utilizzatore_compr_nome: editingVehicle.utilizzatore_compr_nome,
        utilizzatore_compr_indirizzo: editingVehicle.utilizzatore_compr_indirizzo,
        pneumatici: (editingVehicle as any).pneumatici ?? [],
      })
      if (editingVehicle.marca) setSelectedMarca(editingVehicle.marca)
      if ((editingVehicle as any).pneumatici?.length) setPneumaticiPanelOpen(['pneumatici'])
    } else if (open && !editingVehicle) {
      form.resetFields()
    }
  }, [open, editingVehicle, form])

  // Cleanup preview URL on modal close
  useEffect(() => {
    if (!librettoModalOpen && previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
  }, [librettoModalOpen])

  const handleFileSelect = (file: File) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    const url = URL.createObjectURL(file)
    previewUrlRef.current = url
    setLibrettoFile(file)
    setLibrettoPreview(url)
  }

  const handleAnalizza = async () => {
    if (!librettoFile) return
    try {
      const data = await analizza(librettoFile)

      const fieldValues: Record<string, any> = {}
      if (data.targa) fieldValues.targa = data.targa
      if (data.telaio) fieldValues.telaio = data.telaio
      if (data.marca) { fieldValues.marca = data.marca; setSelectedMarca(data.marca) }
      if (data.modello) fieldValues.modello = data.modello
      if (data.carburante) fieldValues.carburante = data.carburante
      if (data.kw != null) fieldValues.kw = data.kw
      if (data.cv != null) fieldValues.cv = data.cv
      if (data.porte) fieldValues.porte = data.porte
      if (data.posti) fieldValues.posti = data.posti
      if (data.prima_immatricolazione) {
        fieldValues.prima_immatricolazione = data.prima_immatricolazione
        const anno = parseInt(data.prima_immatricolazione.split('-')[0])
        if (anno >= 1900 && anno <= 2100) fieldValues.anno = anno
      }
      if (data.data_immatricolazione_attuale) fieldValues.data_immatricolazione_attuale = data.data_immatricolazione_attuale
      if (data.cilindrata_cc != null) fieldValues.cilindrata = `${data.cilindrata_cc} cc`
      if (data.codice_motore) fieldValues.codice_motore = data.codice_motore
      if (data.emissioni_co2_gkm != null) fieldValues.emissioni_co2 = data.emissioni_co2_gkm
      if (data.classe_emissioni) fieldValues.classe_emissioni = data.classe_emissioni
      if (data.carrozzeria) fieldValues.carrozzeria = data.carrozzeria
      if (data.massa_max_kg != null) fieldValues.massa_max_kg = data.massa_max_kg
      if (data.categoria_veicolo) fieldValues.categoria_veicolo = data.categoria_veicolo
      if (data.intestatario_cognome) fieldValues.intestatario_cognome = data.intestatario_cognome
      if (data.intestatario_nome) fieldValues.intestatario_nome = data.intestatario_nome
      if (data.intestatario_indirizzo) fieldValues.intestatario_indirizzo = data.intestatario_indirizzo
      if (data.intestatario_compr_cognome) fieldValues.intestatario_compr_cognome = data.intestatario_compr_cognome
      if (data.intestatario_compr_nome) fieldValues.intestatario_compr_nome = data.intestatario_compr_nome
      if (data.intestatario_compr_indirizzo) fieldValues.intestatario_compr_indirizzo = data.intestatario_compr_indirizzo
      if (data.proprietario_cognome) fieldValues.proprietario_cognome = data.proprietario_cognome
      if (data.proprietario_nome) fieldValues.proprietario_nome = data.proprietario_nome
      if (data.proprietario_indirizzo) fieldValues.proprietario_indirizzo = data.proprietario_indirizzo
      if (data.proprietario_compr_cognome) fieldValues.proprietario_compr_cognome = data.proprietario_compr_cognome
      if (data.proprietario_compr_nome) fieldValues.proprietario_compr_nome = data.proprietario_compr_nome
      if (data.proprietario_compr_indirizzo) fieldValues.proprietario_compr_indirizzo = data.proprietario_compr_indirizzo
      if (data.utilizzatore_cognome) fieldValues.utilizzatore_cognome = data.utilizzatore_cognome
      if (data.utilizzatore_nome) fieldValues.utilizzatore_nome = data.utilizzatore_nome
      if (data.utilizzatore_indirizzo) fieldValues.utilizzatore_indirizzo = data.utilizzatore_indirizzo
      if (data.utilizzatore_compr_cognome) fieldValues.utilizzatore_compr_cognome = data.utilizzatore_compr_cognome
      if (data.utilizzatore_compr_nome) fieldValues.utilizzatore_compr_nome = data.utilizzatore_compr_nome
      if (data.utilizzatore_compr_indirizzo) fieldValues.utilizzatore_compr_indirizzo = data.utilizzatore_compr_indirizzo
      fieldValues.pneumatici = data.pneumatici ?? []
      if (fieldValues.pneumatici.length > 0) setPneumaticiPanelOpen(['pneumatici'])

      form.setFieldsValue(fieldValues)

      const keyFields = [data.marca, data.modello, data.targa, data.carburante, data.kw]
      const nullCount = keyFields.filter(f => f == null).length
      setOcrParziale(nullCount >= 3)
      setOcrAlertVisible(true)

      closeLibrettoModal()
    } catch (err: any) {
      if (err?.response?.status === 503 || !err?.response) {
        message.error('Analisi non disponibile. Inserisci i dati manualmente.')
      }
    }
  }

  const closeLibrettoModal = () => {
    setLibrettoModalOpen(false)
    setLibrettoFile(null)
    setLibrettoPreview(null)
  }

  const handleMarcaChange = (value: string) => {
    // Aggiorna solo il form field durante la digitazione — NON aggiorna selectedMarca
    // per evitare una query modelli per ogni lettera digitata
    form.setFieldValue('marca', value)
  }

  const handleMarcaSelect = (value: string) => {
    // Scatta solo quando l'utente sceglie dalla lista o conferma con Enter/blur
    setSelectedMarca(value)
    form.setFieldValue('modello', undefined)
    form.setFieldValue('marca', value)
  }

  const handleSubmit = async (values: any) => {
    try {
      const cleanedValues = Object.fromEntries(
        Object.entries(values)
          .filter(([_, v]) => v !== undefined)
          .map(([k, v]) => {
            if (typeof v === 'string' && v === '') return [k, null]
            return [k, v]
          })
      )

      let response: Vehicle
      if (editingVehicle) {
        response = await updateMutation.mutateAsync({ id: editingVehicle.id, data: cleanedValues })
      } else {
        response = await createMutation.mutateAsync(cleanedValues as any)
      }
      // Invalida cache marche/modelli per mostrare eventuali nuove voci custom
      queryClient.invalidateQueries({ queryKey: ['auto'] })
      form.resetFields()
      setSelectedMarca('')
      setActiveTab('info')
      setOcrAlertVisible(false)
      setPneumaticiPanelOpen([])
      onSuccess(response)
      onCancel()
    } catch (error: any) {
      const httpStatus = error?.response?.status
      const detail = error?.response?.data?.detail

      if (httpStatus === 409 && detail?.code === 'TARGA_DUPLICATA') {
        message.error(`Targa ${detail.targa} già presente nel sistema. Selezionala dalla lista veicoli.`)
        return
      }

      // messaggio di errore generico gestito dall'hook e dall'interceptor axios
    }
  }

  const vehicleFieldToTab: Record<string, string> = {
    targa: 'info', telaio: 'info', marca: 'info', modello: 'info', colore: 'info', km_attuali: 'info',
    anno: 'technical', carburante: 'technical', cilindrata: 'technical', codice_motore: 'technical',
    carrozzeria: 'technical', kw: 'technical', cv: 'technical', porte: 'technical', posti: 'technical',
    prima_immatricolazione: 'technical', data_immatricolazione_attuale: 'technical',
    categoria_veicolo: 'technical', classe_emissioni: 'technical', emissioni_co2: 'technical', massa_max_kg: 'technical',
    intestatario_cognome: 'intestatario', intestatario_nome: 'intestatario', intestatario_indirizzo: 'intestatario',
  }

  const handleVehicleFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[] }[] }) => {
    const firstField = String(errorFields[0]?.name?.[0] ?? '')
    const tab = vehicleFieldToTab[firstField]
    if (tab) setActiveTab(tab)
  }

  const handleCancel = () => {
    form.resetFields()
    setSelectedMarca('')
    setActiveTab('info')
    setOcrAlertVisible(false)
    setPneumaticiPanelOpen([])
    onCancel()
  }

  return (
    <>
      <Modal
        title={editingVehicle ? 'Modifica Veicolo' : 'Nuovo Veicolo'}
        open={open}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        confirmLoading={editingVehicle ? updateMutation.isPending : createMutation.isPending}
        width={750}
        okText="Salva"
        cancelText="Annulla"
      >
        {ocrAlertVisible && (
          <Alert
            type={ocrParziale ? 'warning' : 'info'}
            message={
              ocrParziale
                ? 'Lettura parziale. Verifica tutti i campi prima di salvare.'
                : 'Campi pre-compilati dal libretto. Verifica e correggi se necessario prima di salvare.'
            }
            showIcon
            closable
            onClose={() => setOcrAlertVisible(false)}
            style={{ marginBottom: 12 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={handleVehicleFinishFailed}
          initialValues={editingVehicle ? { ...editingVehicle } : {}}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'info',
                label: 'Informazioni',
                children: (
                  <>
                    <div style={{ marginBottom: 12, textAlign: 'right' }}>
                      <Button
                        icon={<FileTextOutlined />}
                        onClick={() => setLibrettoModalOpen(true)}
                      >
                        Leggi Libretto
                      </Button>
                    </div>

                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item
                          name="targa"
                          label="Targa"
                          rules={[
                            { required: true, message: 'Inserisci la targa' },
                            { max: 10, message: 'La targa può avere al massimo 10 caratteri' },
                          ]}
                        >
                          <Input
                            size="small"
                            placeholder="XX000XX"
                            maxLength={10}
                            onChange={(e) => form.setFieldValue('targa', e.target.value.toUpperCase())}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="telaio" label="VIN / Telaio">
                          <Input size="small" maxLength={17} placeholder="17 caratteri" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item name="marca" label="Marca">
                          <AutoComplete
                            size="small"
                            placeholder="Seleziona o digita"
                            onChange={handleMarcaChange}
                            onSelect={handleMarcaSelect}
                            onBlur={() => {
                              const v = form.getFieldValue('marca')
                              if (v && v !== selectedMarca) handleMarcaSelect(v)
                            }}
                            filterOption={(input, option) =>
                              (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            notFoundContent={loadingMarche ? <Spin size="small" /> : null}
                            options={marcheData?.marche.map((marca) => ({
                              value: marca,
                              label: marca,
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="modello" label="Modello">
                          <AutoComplete
                            size="small"
                            placeholder="Seleziona o digita"
                            filterOption={(input, option) =>
                              (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            notFoundContent={loadingModelli ? <Spin size="small" /> : null}
                            options={modelliData?.modelli.map((modello) => ({
                              value: modello,
                              label: modello,
                            }))}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item name="colore" label="Colore">
                          <Input size="small" placeholder="es. Nero" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="km_attuali" label="Km attuali">
                          <InputNumber size="small" min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                ),
              },
              {
                key: 'technical',
                label: 'Dati Tecnici',
                children: (
                  <>
                    <Row gutter={8}>
                      <Col span={6}>
                        <Form.Item name="anno" label="Anno">
                          <InputNumber size="small" min={1900} max={new Date().getFullYear() + 1} style={{ width: '100%' }} placeholder="es. 2020" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="carburante" label="Carburante">
                          <Select
                            size="small"
                            placeholder="Tipo"
                            allowClear
                            options={carburanti?.map((c) => ({ value: c, label: c }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="cilindrata" label="Cilindrata">
                          <Input size="small" placeholder="es. 1598 cc" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="codice_motore" label="Codice motore">
                          <Input size="small" placeholder="es. N47D20C" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="carrozzeria" label="Carrozzeria">
                          <Input size="small" placeholder="es. berlina" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={8}>
                      <Col span={6}>
                        <Form.Item name="kw" label="KW">
                          <InputNumber size="small" min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="cv" label="CV">
                          <InputNumber size="small" min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="porte" label="Porte">
                          <Select
                            size="small"
                            placeholder="N."
                            allowClear
                            options={[2, 3, 4, 5].map(n => ({ value: n, label: String(n) }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="posti" label="Posti">
                          <InputNumber size="small" min={1} max={20} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={8}>
                      <Col span={8}>
                        <Form.Item name="prima_immatricolazione" label="Prima immatr.">
                          <Input size="small" type="date" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="data_immatricolazione_attuale" label="Immatr. attuale">
                          <Input size="small" type="date" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="categoria_veicolo" label="Categoria">
                          <Input size="small" placeholder="es. M1, N1" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={8}>
                      <Col span={8}>
                        <Form.Item name="classe_emissioni" label="Classe Euro">
                          <Input size="small" placeholder="es. Euro 6" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="emissioni_co2" label="CO₂ (g/km)">
                          <InputNumber size="small" min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="massa_max_kg" label="Massa max (kg)">
                          <InputNumber size="small" min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                ),
              },
              {
                key: 'intestatario',
                label: 'Intestatario',
                children: (
                  <>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                      Dati rilevati dal libretto di circolazione (C.1, C.2, C.3)
                    </Typography.Text>

                    {/* C.1 — Intestatario del libretto */}
                    <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8, color: token.colorPrimary }}>
                      C.1 — Intestatario del libretto di circolazione
                    </Typography.Text>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item name="intestatario_cognome" label="Cognome / Ragione sociale">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="intestatario_nome" label="Nome">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="intestatario_indirizzo" label="Indirizzo">
                      <Input size="small" autoComplete="off" />
                    </Form.Item>
                    <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                      (COMPR) — Comproprietario C.1
                    </Typography.Text>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item name="intestatario_compr_cognome" label="Cognome / Ragione sociale">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="intestatario_compr_nome" label="Nome">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="intestatario_compr_indirizzo" label="Indirizzo">
                      <Input size="small" autoComplete="off" />
                    </Form.Item>

                    {/* C.2 — Proprietario del veicolo */}
                    <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8, marginTop: 12, color: token.colorPrimary }}>
                      C.2 — Proprietario del veicolo
                    </Typography.Text>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item name="proprietario_cognome" label="Cognome / Ragione sociale">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="proprietario_nome" label="Nome">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="proprietario_indirizzo" label="Indirizzo">
                      <Input size="small" autoComplete="off" />
                    </Form.Item>
                    <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                      (COMPR) — Comproprietario C.2
                    </Typography.Text>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item name="proprietario_compr_cognome" label="Cognome / Ragione sociale">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="proprietario_compr_nome" label="Nome">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="proprietario_compr_indirizzo" label="Indirizzo">
                      <Input size="small" autoComplete="off" />
                    </Form.Item>

                    {/* C.3 — Utilizzatore del veicolo */}
                    <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8, marginTop: 12, color: token.colorPrimary }}>
                      C.3 — Utilizzatore del veicolo
                    </Typography.Text>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item name="utilizzatore_cognome" label="Cognome / Ragione sociale">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="utilizzatore_nome" label="Nome">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="utilizzatore_indirizzo" label="Indirizzo">
                      <Input size="small" autoComplete="off" />
                    </Form.Item>
                    <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                      (COMPR) — Comproprietario C.3
                    </Typography.Text>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item name="utilizzatore_compr_cognome" label="Cognome / Ragione sociale">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="utilizzatore_compr_nome" label="Nome">
                          <Input size="small" autoComplete="off" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="utilizzatore_compr_indirizzo" label="Indirizzo">
                      <Input size="small" autoComplete="off" />
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'pneumatici',
                label: 'Pneumatici',
                children: (
                  <Collapse
                    activeKey={pneumaticiPanelOpen}
                    onChange={(keys) => setPneumaticiPanelOpen(keys as string[])}
                    ghost
                    items={[{
                      key: 'pneumatici',
                      label: <Typography.Text style={{ fontSize: 13 }}>Pneumatici omologati</Typography.Text>,
                      children: (
                        <Form.List name="pneumatici">
                          {(fields, { add, remove }) => (
                            <Form.Item noStyle shouldUpdate>
                              {({ getFieldValue, setFieldValue }) => {
                                const allPneu: any[] = getFieldValue('pneumatici') ?? []

                                const handleInUsoChange = (index: number, checked: boolean) => {
                                  const stagione = allPneu[index]?.stagione
                                  // 4stagioni non soggetti al vincolo
                                  if (stagione === '4stagioni') {
                                    setFieldValue(['pneumatici', index, 'in_uso'], checked)
                                    return
                                  }
                                  if (checked) {
                                    // Deseleziona eventuali altri della stessa stagione
                                    const updated = allPneu.map((p: any, i: number) => {
                                      if (i === index) return { ...p, in_uso: true }
                                      if (p.stagione === stagione && p.stagione !== '4stagioni') return { ...p, in_uso: false }
                                      return p
                                    })
                                    setFieldValue('pneumatici', updated)
                                  } else {
                                    setFieldValue(['pneumatici', index, 'in_uso'], false)
                                  }
                                }

                                return (
                                  <>
                                    {fields.map(({ key, name, ...restField }) => {
                                      const stagione = allPneu[name]?.stagione
                                      const inUso = allPneu[name]?.in_uso ?? false
                                      const stagioneLabel = stagione === 'estivo' ? 'estivo' : stagione === 'invernale' ? 'invernale' : null
                                      const tooltipMsg = stagioneLabel
                                        ? `Segna come pneumatico ${stagioneLabel} in uso (deseleziona automaticamente l'altro ${stagioneLabel})`
                                        : 'Segna come pneumatico in uso'

                                      return (
                                        <div key={key} style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginBottom: 8, flexWrap: 'wrap' }}>
                                          <Form.Item {...restField} name={[name, 'misura_completa']} label="Misura" style={{ flex: '2 1 130px', marginBottom: 0 }}>
                                            <Input size="small" placeholder="es. 205/55R16 91V" />
                                          </Form.Item>
                                          <Form.Item {...restField} name={[name, 'larghezza_mm']} label="Larg." style={{ flex: '1 1 56px', marginBottom: 0 }}>
                                            <InputNumber size="small" min={0} style={{ width: '100%' }} placeholder="205" />
                                          </Form.Item>
                                          <Form.Item {...restField} name={[name, 'spalla_pct']} label="Spalla" style={{ flex: '1 1 56px', marginBottom: 0 }}>
                                            <InputNumber size="small" min={0} style={{ width: '100%' }} placeholder="55" />
                                          </Form.Item>
                                          <Form.Item {...restField} name={[name, 'diametro_in']} label='Diam."' style={{ flex: '1 1 52px', marginBottom: 0 }}>
                                            <InputNumber size="small" min={0} style={{ width: '100%' }} placeholder="16" />
                                          </Form.Item>
                                          <Form.Item {...restField} name={[name, 'indice_carico']} label="I.C." style={{ flex: '1 1 48px', marginBottom: 0 }}>
                                            <Input size="small" placeholder="91" />
                                          </Form.Item>
                                          <Form.Item {...restField} name={[name, 'codice_velocita']} label="Vel." style={{ flex: '1 1 44px', marginBottom: 0 }}>
                                            <Input size="small" placeholder="V" maxLength={3} />
                                          </Form.Item>
                                          <Form.Item {...restField} name={[name, 'stagione']} label="Stagione" style={{ flex: '1 1 100px', marginBottom: 0 }}>
                                            <Select size="small" allowClear placeholder="–" options={[
                                              { value: 'estivo', label: 'Estivo' },
                                              { value: 'invernale', label: 'Invernale' },
                                              { value: '4stagioni', label: '4 Stagioni' },
                                            ]} />
                                          </Form.Item>
                                          <Tooltip title={tooltipMsg}>
                                            <Form.Item name={[name, 'in_uso']} valuePropName="checked" label="In uso" style={{ marginBottom: 0 }}>
                                              <Checkbox
                                                checked={inUso}
                                                onChange={(e) => handleInUsoChange(name, e.target.checked)}
                                              />
                                            </Form.Item>
                                          </Tooltip>
                                          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remove(name)} style={{ marginBottom: 0 }} />
                                        </div>
                                      )
                                    })}
                                    <Button
                                      type="dashed"
                                      size="small"
                                      onClick={() => { add({ in_uso: false }); setPneumaticiPanelOpen(['pneumatici']) }}
                                      icon={<PlusOutlined />}
                                      style={{ marginTop: 4 }}
                                    >
                                      Aggiungi misura
                                    </Button>
                                  </>
                                )
                              }}
                            </Form.Item>
                          )}
                        </Form.List>
                      ),
                    }]}
                  />
                ),
              },
              {
                key: 'altro',
                label: 'Altro',
                children: (
                  <>
                    <Form.Item name="courtesy_car" valuePropName="checked">
                      <Checkbox>Auto di cortesia</Checkbox>
                    </Form.Item>
                    <Form.Item name="note" label="Note">
                      <VoiceTextarea rows={3} minHeight={90} maxHeight={200} label="Note veicolo" compact />
                    </Form.Item>
                  </>
                ),
              },
              ...(editingVehicle ? [{
                key: 'integrazioni',
                label: <><ApiOutlined /> Integrazioni</>,
                children: (() => {
                  const infocarUrl = integrationSettings['infocar_repair_url'] || null
                  const targaVal = editingVehicle.targa ?? ''
                  const telaioVal = editingVehicle.telaio ?? ''
                  const urlConTarga = infocarUrl
                    ? `${infocarUrl}${infocarUrl.includes('?') ? '&' : '?'}targa=${encodeURIComponent(targaVal)}`
                    : undefined

                  return (
                    <>
                      <PannelloIntegrazioneEsterna
                        titolo="Infocar Repair"
                        descrizione="Apri la scheda del veicolo in Infocar Repair. I dati identificativi sono copiabili con un click."
                        url={urlConTarga}
                        urlLabel="Apri in Infocar"
                        campi={[
                          {
                            etichetta: 'Targa',
                            chiave: 'targa_display',
                            tipo: 'string',
                            valore: targaVal || null,
                            readonly: true,
                            copiabile: true,
                          },
                          {
                            etichetta: 'Telaio / VIN',
                            chiave: 'telaio_display',
                            tipo: 'string',
                            valore: telaioVal || null,
                            readonly: true,
                            copiabile: true,
                          },
                          {
                            etichetta: 'URL Infocar configurato',
                            chiave: 'infocar_url_display',
                            tipo: 'url',
                            valore: infocarUrl,
                            readonly: true,
                            copiabile: false,
                            descrizione: infocarUrl
                              ? undefined
                              : 'Non configurato — vai su Impostazioni → Integrazioni',
                          },
                        ]}
                      />
                      {!infocarUrl && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          <a href="/integrazioni" target="_blank" rel="noopener noreferrer">
                            Configura l&apos;URL di Infocar Repair
                          </a>{' '}
                          nella pagina Integrazioni per abilitare il pulsante &quot;Apri in Infocar&quot;.
                        </Typography.Text>
                      )}
                    </>
                  )
                })(),
              }] : []),
            ]}
          />
        </Form>
      </Modal>

      {/* Modal carica libretto */}
      <Modal
        title="Carica libretto di circolazione"
        open={librettoModalOpen}
        onCancel={closeLibrettoModal}
        footer={
          librettoFile
            ? [
                <Button key="cancel" onClick={closeLibrettoModal}>Annulla</Button>,
                <Button key="analizza" type="primary" loading={isOcrLoading} onClick={handleAnalizza}>
                  Analizza
                </Button>,
              ]
            : null
        }
        width={480}
      >
        {!librettoFile ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              Carica il PDF del libretto digitale oppure una foto/immagine del documento cartaceo.
            </Typography.Text>
            <Row gutter={12}>
              <Col span={8}>
                <label style={{ cursor: 'pointer', display: 'block' }}>
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                  />
                  <Card hoverable style={{ textAlign: 'center', padding: '16px 0' }}>
                    <FilePdfOutlined style={{ fontSize: 32, color: token.colorError, display: 'block', marginBottom: 8 }} />
                    <span>Carica PDF</span>
                  </Card>
                </label>
              </Col>
              <Col span={8}>
                <label style={{ cursor: 'pointer', display: 'block' }}>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                  />
                  <Card hoverable style={{ textAlign: 'center', padding: '16px 0' }}>
                    <FolderOpenOutlined style={{ fontSize: 32, color: token.colorPrimary, display: 'block', marginBottom: 8 }} />
                    <span>Carica immagine</span>
                  </Card>
                </label>
              </Col>
              <Col span={8}>
                <Card
                  hoverable
                  style={{ textAlign: 'center', padding: '16px 0', cursor: 'pointer' }}
                  onClick={() => { setCameraOpen(true); setTimeout(() => startCamera(), 100) }}
                >
                  <CameraOutlined style={{ fontSize: 32, color: token.colorSuccess, display: 'block', marginBottom: 8 }} />
                  <span>Scatta foto</span>
                </Card>
              </Col>
            </Row>
          </Space>
        ) : (
          <Spin spinning={isOcrLoading} tip="Lettura in corso...">
            {librettoFile.type === 'application/pdf' ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <FilePdfOutlined style={{ fontSize: 64, color: token.colorError, display: 'block', marginBottom: 12 }} />
                <Typography.Text strong>{librettoFile.name}</Typography.Text>
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                  {(librettoFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography.Text>
              </div>
            ) : (
              <img
                src={librettoPreview!}
                alt="Libretto"
                style={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 4 }}
              />
            )}
          </Spin>
        )}
      </Modal>

      {/* Camera modal per scattare foto del libretto */}
      <Modal
        title="Scatta foto libretto"
        open={cameraOpen}
        onCancel={() => { stopCamera(); setCameraOpen(false) }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <div style={{ position: 'relative', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
          {!capturedFrame ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '100%', maxHeight: 400, display: 'block' }}
            />
          ) : (
            <img src={capturedFrame} alt="preview" style={{ width: '100%', maxHeight: 400, display: 'block', objectFit: 'contain' }} />
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
        <Space style={{ marginTop: 12, width: '100%', justifyContent: 'center' }} size="middle">
          {!capturedFrame ? (
            <>
              <Button icon={<CameraOutlined />} type="primary" size="large" onClick={snapPhoto}>
                Scatta
              </Button>
              <Button onClick={switchCamera}>
                Cambia fotocamera
              </Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={confirmCameraPhoto}>
                Usa questa foto
              </Button>
              <Button onClick={() => { setCapturedFrame(null); startCamera() }}>
                Riprova
              </Button>
            </>
          )}
        </Space>
      </Modal>
    </>
  )
}

export default VehicleFormModal
